import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  InputAdornment,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { updatePharmacyContactInfo } from "../../api/provider";

export const emailValidationRegex =
  /^[A-Za-z0-9]+([._%+-][A-Za-z0-9]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const Field = ({
  label,
  name,
  value,
  handleChange,
  disabled,
  type,
  options,
  placeholder,
  error,
  helperText,
  charLimit,
  sx = {},
}) => (
  <Box flex={1}>
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: "14px",
        lineHeight: "35px",
        color: "#403C6F",
      }}
    >
      {label}
    </Typography>
    {type === "select" ? (
      <Select
        fullWidth
        name={name}
        value={value}
        onChange={handleChange}
        displayEmpty
        size="small"
        sx={{
          height: "45px",
          background: "#fff",
          borderRadius: "8px",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
          ...(disabled
            ? {
              backgroundColor: "#E9E9E8",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E0E0E0",
              },
            }
            : {}),
          ...sx,
        }}
        renderValue={(selected) => selected || `Select ${label}`}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    ) : (
      <TextField
        fullWidth
        name={name}
        value={value}
        onChange={handleChange}
        size="small"
        placeholder={placeholder || `Enter ${label}`}
        disabled={disabled}
        error={error}
        helperText={helperText}
        slotProps={{
          input: charLimit
            ? {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "rgb(102, 102, 102)",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",

                      mr: 0.5,
                    }}
                  >
                    {(value || "").length}/{charLimit}
                  </Typography>
                </InputAdornment>
              ),
            }
            : undefined,
          formHelperText: {
            sx: {
              color: "#d32f2f",
              marginLeft: 0,
              marginTop: "4px",
              fontSize: "0.75rem",
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "45px",
            "& fieldset": {
              borderColor: "#E0E0E0",
              borderRadius: "8px",
            },
            ...(disabled
              ? {
                backgroundColor: "#E9E9E8",
                "&:hover fieldset": {
                  borderColor: "#E0E0E0",
                },
              }
              : {}),
            ...sx["& .MuiOutlinedInput-root"],
          },
          ...(charLimit
            ? {
              "& .MuiOutlinedInput-input": {
                pr: 0,
              },
            }
            : {
              "& .MuiOutlinedInput-input": {
                paddingRight: "60px",
              },
            }),
          "& .MuiOutlinedInput-root.Mui-disabled": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E0E0E0",
            },
          },
          ...sx,
        }}
      />
    )}
  </Box>
);

const Row = ({
  label,
  value,
  disableEllipsis = false,
  alignRight = false,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      mb: 1.5,
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
      textAlign: alignRight ? "right" : "left",
    }}
  >
    <Typography
      variant="body2"
      component="div"
      sx={{
        color: "#7F7C9F",
        fontSize: "12px",
        fontWeight: 500,
        mb: 0.5,
        whiteSpace: "nowrap",
        lineHeight: "16px",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="body2"
        component="div"
        sx={{
          color: "#403C6F",
          fontSize: "14px",
          fontWeight: 600,
          whiteSpace: disableEllipsis ? "normal" : "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: "20px",
          width: "100%",
        }}
        title={value}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disableEllipsis: PropTypes.bool,
  alignRight: PropTypes.bool,
};

Field.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChange: PropTypes.func,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  charLimit: PropTypes.number,
  sx: PropTypes.object,
};



const safeGet = (respVal, formVal) => {
  if (respVal !== undefined && respVal !== null) {
    return respVal;
  } else if (formVal === undefined) {
    return null;
  } else {
    return formVal;
  }
};

const createLocation2Payload = (formData) => {
  return {
    phoneNumber:
      formData.location2PhoneNumber === ""
        ? null
        : formData.location2PhoneNumber || null,
    email:
      formData.location2Email === "" ? null : formData.location2Email || null,
    faxNumber:
      formData.location2FaxNumber === ""
        ? null
        : formData.location2FaxNumber || null,
    address1:
      formData.location2AddressLine1 === ""
        ? null
        : formData.location2AddressLine1 || null,
    address2:
      formData.location2AddressLine2 === ""
        ? null
        : formData.location2AddressLine2 || null,
    city:
      formData.location2City === "" ? null : formData.location2City || null,
    state:
      formData.location2State === "" ? null : formData.location2State || null,
    zipCode:
      formData.location2ZipCode === ""
        ? null
        : formData.location2ZipCode || null,
  };
};

const ContactPharmacyInfo = ({
  contactInfo = {},
  pharmacyId,
  onSave: onUpdate,
}) => {
  const [open, setOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const formatPrimaryAddress = (ci) => {
    if (!ci) return "";
    const addr = ci.address || ci.contact?.address || {};
    if (typeof addr === "string") return addr;
    if (!addr || typeof addr !== "object") return "";
    const line1 = addr.addressLine1 || addr.street || "";
    const line2 = addr.addressLine2 || addr.address2 || "";
    const city = addr.city || "";
    const state = addr.state || "";
    const zip = addr.zip || addr.zipCode || "";
    const parts = [line1, line2, city, state].filter((p) => p && p !== "N/A");
    return [parts.join(", "), zip].filter(Boolean).join(" ").trim();
  };

  const [formData, setFormData] = useState({
    phoneNumber: contactInfo.contact?.phone || contactInfo.phone || "",
    email: contactInfo.contact?.email || contactInfo.email || "",
    faxNumber: contactInfo.contact?.fax || contactInfo.faxNumber || "",
    address: formatPrimaryAddress(contactInfo),

    location2PhoneNumber: contactInfo.location2?.phoneNumber || "",
    location2Email: contactInfo.location2?.email || "",
    location2FaxNumber: contactInfo.location2?.faxNumber || "",
    location2AddressLine1: contactInfo.location2?.address?.street || "",
    location2AddressLine2: contactInfo.location2?.address?.address2 || "",
    location2City: contactInfo.location2?.address?.city || "",
    location2State: contactInfo.location2?.address?.state || "",
    location2ZipCode: contactInfo.location2?.address?.zipCode || "",
  });

  const [initialFormData, setInitialFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const initialData = {
      phoneNumber: contactInfo.contact?.phone || contactInfo.phone || "",
      email: contactInfo.contact?.email || contactInfo.email || "",
      faxNumber: contactInfo.contact?.fax || contactInfo.faxNumber || "",
      address: formatPrimaryAddress(contactInfo),

      location2PhoneNumber: contactInfo.location2?.phoneNumber || "",
      location2Email: contactInfo.location2?.email || "",
      location2FaxNumber: contactInfo.location2?.faxNumber || "",
      location2AddressLine1: contactInfo.location2?.address?.street || "",
      location2AddressLine2: contactInfo.location2?.address?.address2 || "",
      location2City: contactInfo.location2?.address?.city || "",
      location2State: contactInfo.location2?.address?.state || "",
      location2ZipCode: contactInfo.location2?.address?.zipCode || "",
    };

    setInitialFormData(initialData);
    setFormData(initialData);
  }, [contactInfo]);

  useEffect(() => {
    if (Object.keys(initialFormData).length === 0) return;

    const hasChanges = Object.keys(initialFormData).some((key) => {
      if (key.startsWith("location2")) {
        const currentValue = formData[key] || "";
        const initialValue = initialFormData[key] || "";
        return currentValue !== initialValue;
      }
      return formData[key] !== initialFormData[key];
    });

    setIsFormChanged(hasChanges);
  }, [formData, initialFormData]);

  const handleOpen = () => {
    const location2Address = contactInfo.location2?.address || {};

    setFormData({
      phoneNumber: contactInfo.contact?.phone || contactInfo.phone || "",
      email: contactInfo.contact?.email || contactInfo.email || "",
      faxNumber: contactInfo.contact?.fax || contactInfo.faxNumber || "",
      address: formatPrimaryAddress(contactInfo),

      location2PhoneNumber: contactInfo.location2?.phoneNumber || "",
      location2Email: contactInfo.location2?.email || "",
      location2FaxNumber: contactInfo.location2?.faxNumber || "",
      location2AddressLine1:
        location2Address.street || location2Address.addressLine1 || "",
      location2AddressLine2:
        location2Address.address2 || location2Address.addressLine2 || "",
      location2City: location2Address.city || "",
      location2State: location2Address.state || "",
      location2ZipCode: location2Address.zipCode || location2Address.zip || "",
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setValidationErrors({});
  };

  const validateAddressLines = (line1, line2) => {
    if (
      line1 &&
      line2 &&
      line1.trim().toLowerCase() === line2.trim().toLowerCase()
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        location2AddressLine1: "Address Line 1 and Line 2 cannot be the same",
        location2AddressLine2: "Address Line 1 and Line 2 cannot be the same",
      }));
      return false;
    } else {
      if (
        validationErrors.location2AddressLine1 ===
        "Address Line 1 and Line 2 cannot be the same"
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          location2AddressLine1: "",
          location2AddressLine2: "",
        }));
      }
      return true;
    }
  };

  const handleEmailChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    if (value && value.trim() !== "") {
      if (emailValidationRegex.test(value)) {
        setValidationErrors((prev) => ({ ...prev, [name]: "" }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          [name]: "Please enter a valid email address",
        }));
      }
    } else {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneFaxChange = (name, value) => {
    const digitsOnly = value.replaceAll(/\D/g, "").slice(0, 10);
    setFormData({
      ...formData,
      [name]: digitsOnly,
    });
    const label = name.includes("Fax") ? "Fax number" : "Phone number";
    if (digitsOnly.length === 0) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    } else if (digitsOnly.length === 10) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: `${label} must be exactly 10 digits`,
      }));
    }
  };

  const handleAddressLineChange = (name, value) => {
    const LIMIT = 60;
    let newValue = value.length > LIMIT ? value.slice(0, LIMIT) : value;
    const updatedFormData = {
      ...formData,
      [name]: newValue,
    };
    validateAddressLines(
      name === "location2AddressLine1"
        ? newValue
        : updatedFormData.location2AddressLine1,
      name === "location2AddressLine2"
        ? newValue
        : updatedFormData.location2AddressLine2
    );
    setFormData(updatedFormData);
  };

  const handleCityStateZipChange = (name, value) => {
    let newValue = value;
    if (name === "location2City" || name === "location2State") {
      newValue = newValue.replaceAll(/[^A-Za-z ]+/g, "");
      if (newValue.length > 20) newValue = newValue.slice(0, 20);
    }
    if (name === "location2ZipCode") {
      newValue = newValue.replaceAll(/\D/g, "").slice(0, 5);
    }
    setFormData({
      ...formData,
      [name]: newValue,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("location2")) {
      setIsDirty(true);
    }
    if (name === "location2Email") {
      handleEmailChange(name, value);
      return;
    }
    if (
      name === "phoneNumber" ||
      name === "location2PhoneNumber" ||
      name === "location2FaxNumber"
    ) {
      handlePhoneFaxChange(name, value);
      return;
    }
    if (name === "location2AddressLine1" || name === "location2AddressLine2") {
      handleAddressLineChange(name, value);
      return;
    }
    if (
      name === "location2City" ||
      name === "location2State" ||
      name === "location2ZipCode"
    ) {
      handleCityStateZipChange(name, value);
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = async () => {
    try {
      const isAddressValid = validateAddressLines(
        formData.location2AddressLine1,
        formData.location2AddressLine2
      );

      const hasErrors =
        !isAddressValid ||
        Object.values(validationErrors).some(Boolean);

      if (hasErrors) {
        return;
      }

      if (!isFormChanged && !isDirty) {
        handleClose();
        return;
      }

      setIsLoading(true);

      try {
        const location2Payload = createLocation2Payload(formData);
        const apiPayload = { location2: location2Payload };

        const response = await updatePharmacyContactInfo(
          pharmacyId,
          apiPayload,
        );

        const line1 = safeGet(
          response.location2?.address1,
          location2Payload.address1,
        );
        const line2 = safeGet(
          response.location2?.address2,
          location2Payload.address2,
        );
        const city = safeGet(response.location2?.city, location2Payload.city);
        const state = safeGet(
          response.location2?.state,
          location2Payload.state,
        );
        const zip = safeGet(
          response.location2?.zipCode,
          location2Payload.zipCode,
        );
        const fullAddressParts = [
          [line1, line2].filter(Boolean).join(", "),
          [city, state].filter(Boolean).join(", "),
          zip || "",
        ].filter((p) => p && p.trim() !== "");
        const fullAddress = fullAddressParts.length
          ? fullAddressParts.join(", ").trim()
          : null;
        const updatedContactInfo = {
          ...contactInfo,
          location2: {
            phoneNumber: safeGet(
              response.location2?.phoneNumber,
              location2Payload.phoneNumber,
            ),
            email: safeGet(response.location2?.email, location2Payload.email),
            faxNumber: safeGet(
              response.location2?.faxNumber,
              location2Payload.faxNumber,
            ),
            address: {
              addressLine1: line1 || null,
              addressLine2: line2 || null,
              city: city || null,
              state: state || null,
              zipCode: zip || null,
              fullAddress: fullAddress,
            },
          },
        };

        if (onUpdate) {
          onUpdate(updatedContactInfo);
        }

        handleClose();
      } catch (apiError) {
        console.error("Unexpected error", apiError);
        setValidationErrors({
          general: "Failed to update contact information. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Unexpected error", error);
      if (error instanceof Error) {
        // Error handled by resetting loading state
      }
      setIsLoading(false);
    }
  };



  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        backgroundColor: "white",
        padding: "16px 16px 24px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        height: "588px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #E0E0E0",
          pb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#080A0E",
            lineHeight: "150%",
          }}
        >
          Contact Information
        </Typography>
        <IconButton
          onClick={handleOpen}
          aria-label="Edit pharmacy contact information"
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "6px",
            border: "1px solid #E8E8E9",
            "&:hover": {
              backgroundColor: "rgba(232, 232, 233, 0.2)",
            },
          }}
        >
          <EditOutlinedIcon sx={{ color: "#6E6D92", fontSize: "20px" }} />
        </IconButton>
      </Box>

      <Box sx={{ width: "100%", height: "100%", overflowY: "auto", pr: 1 }}>
        <Box display="flex" alignItems="center" width="100%" mb={1.5}>
          <Typography
            sx={{
              fontSize: "16px",
              color: "#403C6F",
              whiteSpace: "nowrap",
            }}
          >
            Location 1
          </Typography>
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "#E9E9E8",
              ml: 0.6,
            }}
          />
          <Divider sx={{ flex: 1, borderColor: "#E9E9E8" }} />
        </Box>

        <Box display="flex" width="100%" mb={2} sx={{ gap: 2 }}>
          <Box flex={1}>
            <Row
              label="Phone Number"
              value={contactInfo.contact?.phone || contactInfo.phone}
            />
          </Box>
          <Box flex={1} sx={{ textAlign: "right" }}>
            <Row
              label="Email"
              value={contactInfo.contact?.email || contactInfo.email}
              disableEllipsis={true}
              alignRight
            />
          </Box>
        </Box>

        <Box display="flex" width="100%" mb={2} sx={{ gap: 2 }}>
          <Box flex={1}>
            <Row
              label="Address"
              value={`${contactInfo?.address?.street || ""}, ${contactInfo?.address?.city || ""
                }, ${contactInfo?.address?.state || ""} ${contactInfo?.address?.zip || ""
                }`
                .trim()
                .replaceAll(/(?:^,\s*)|(?:,\s*$)/g, "")}
              disableEllipsis={true}
            />
          </Box>
          <Box flex={1} sx={{ textAlign: "right" }}>
            <Row
              label="Fax"
              value={contactInfo.contact?.fax || contactInfo.faxNumber}
              alignRight
            />
          </Box>
        </Box>

        <Box display="flex" alignItems="center" width="100%" mb={1.5} mt={3}>
          <Typography
            sx={{
              fontSize: "16px",
              color: "#403C6F",
              whiteSpace: "nowrap",
            }}
          >
            Location 2
          </Typography>
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "#E0E0E0",
              ml: 0.6,
            }}
          />
          <Divider sx={{ flex: 1, borderColor: "#E9E9E8" }} />
        </Box>

        <Box display="flex" width="100%" mb={2} sx={{ gap: 2 }}>
          <Box flex={1}>
            <Row
              label="Phone Number"
              value={contactInfo.location2?.phoneNumber}
            />
          </Box>
          <Box flex={1} sx={{ textAlign: "right" }}>
            <Row
              label="Email"
              value={contactInfo.location2?.email}
              disableEllipsis={true}
              alignRight
            />
          </Box>
        </Box>

        <Box display="flex" width="100%" mb={2} sx={{ gap: 2 }}>
          <Box flex={1}>
            <Row
              label="Address"
              value={contactInfo.location2?.address?.fullAddress}
              disableEllipsis={true}
            />
          </Box>
          <Box flex={1} sx={{ textAlign: "right" }}>
            <Row
              label="Fax"
              value={contactInfo.location2?.faxNumber}
              alignRight
            />
          </Box>
        </Box>

        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth={false}
          slotProps={{
            paper: {
              sx: {
                width: "900px",
                maxWidth: "90vw",
                maxHeight: "90vh",
                overflow: "hidden",
                overflowX: "hidden",
                borderRadius: "16px",
                p: "24px",
                backgroundColor: "#F7F8FB",
                display: "flex",
                flexDirection: "column",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              m: 0,
              width: "100%",
              height: "40px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: "30px",
                color: "#000000",
              }}
            >
              Edit Contact Information
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon sx={{ color: "#7F7C9F" }} />
            </IconButton>
          </DialogTitle>
          <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pr: 1 }}>
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: "250px",
                borderRadius: "16px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",

                mt: 2,
                p: 2,
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#000000",
                  fontWeight: 600,
                  mb: 1,
                  mt: 1,
                }}
              >
                Location 1
              </Typography>

              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                gap={2}
                width="100%"
                mb={2}
              >
                <Field
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  handleChange={handleChange}
                  placeholder="Enter phone number"
                  disabled={true}
                />
                <Field
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  handleChange={handleChange}
                  placeholder="Enter email"
                  disabled={true}
                />
                <Field
                  label="Fax Number"
                  name="faxNumber"
                  value={formData.faxNumber}
                  handleChange={handleChange}
                  placeholder="Enter fax number"
                  disabled={true}
                />
              </Box>

              <Box display="flex" gap={2} width="100%" mb={2}>
                <Field
                  label="Address"
                  name="address"
                  value={formData.address}
                  handleChange={handleChange}
                  placeholder="Enter address"
                  disabled={true}
                />
              </Box>
            </DialogContent>

            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: "280px",
                borderRadius: "16px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                mt: 2,
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#000000",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Location 2
              </Typography>

              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                gap={2}
                width="100%"
                mb={2}
              >
                <Field
                  label="Phone Number"
                  name="location2PhoneNumber"
                  value={formData.location2PhoneNumber}
                  handleChange={handleChange}
                  placeholder="Enter phone number"
                  error={!!validationErrors.location2PhoneNumber}
                  helperText={validationErrors.location2PhoneNumber}
                  charLimit={10}
                />
                <Field
                  label="Email Address"
                  name="location2Email"
                  value={formData.location2Email}
                  handleChange={handleChange}
                  placeholder="Enter email"
                  error={!!validationErrors.location2Email}
                  helperText={validationErrors.location2Email}
                />
                <Field
                  label="Fax Number"
                  name="location2FaxNumber"
                  value={formData.location2FaxNumber}
                  handleChange={handleChange}
                  placeholder="Enter fax number"
                  error={!!validationErrors.location2FaxNumber}
                  helperText={validationErrors.location2FaxNumber}
                  charLimit={10}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#403C6F",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Address
              </Typography>

              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  p: 2,
                }}
              >
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={2}
                  width="100%"
                  mb={2}
                >
                  <Box sx={{ position: "relative", flex: 1 }}>
                    <Field
                      label="Address Line 1"
                      name="location2AddressLine1"
                      value={formData.location2AddressLine1}
                      handleChange={handleChange}
                      placeholder="Enter address line 1"
                      charLimit={60}
                    />
                    <Box
                      id="add-note-details-count-1"
                      sx={{
                        position: "absolute",
                        bottom: "8px",
                        right: "8px",
                        fontSize: "12px",

                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        pointerEvents: "none",
                      }}
                    >
                      {formData.location2AddressLine1.length}/60
                    </Box>
                  </Box>

                  <Box sx={{ position: "relative", flex: 1 }}>
                    <Field
                      label="Address Line 2"
                      name="location2AddressLine2"
                      value={formData.location2AddressLine2}
                      handleChange={handleChange}
                      placeholder="Enter address line 2 (optional)"
                      error={!!validationErrors.location2AddressLine2}
                      helperText={validationErrors.location2AddressLine2}
                      charLimit={60}
                    />
                    <Box
                      id="add-note-details-count-2"
                      sx={{
                        position: "absolute",
                        bottom: "8px",
                        right: "8px",
                        fontSize: "12px",

                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        pointerEvents: "none",
                      }}
                    >
                      {formData.location2AddressLine2.length}/60
                    </Box>
                  </Box>
                </Box>

                <Box
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={2}
                  width="100%"
                >
                  <Field
                    label="City"
                    name="location2City"
                    value={formData.location2City}
                    handleChange={handleChange}
                    placeholder="Enter city"
                    charLimit={20}
                  />
                  <Field
                    label="State"
                    name="location2State"
                    value={formData.location2State}
                    handleChange={handleChange}
                    placeholder="Enter state"
                    charLimit={20}
                  />
                  <Field
                    label="Zip Code"
                    name="location2ZipCode"
                    value={formData.location2ZipCode}
                    handleChange={handleChange}
                    placeholder="Enter zip code"
                    charLimit={5}
                  />
                </Box>
              </Box>
            </DialogContent>

            {validationErrors.general && (
              <Box sx={{ mt: 2, p: 2 }}>
                <Typography sx={{ color: "#d32f2f", fontSize: "14px" }}>
                  {validationErrors.general}
                </Typography>
              </Box>
            )}
          </Box>
          <DialogActions
            sx={{ justifyContent: "flex-end", gap: "10px", mt: 1 }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: "52px",
                border: "1px solid #9F9DB6",
                color: "#403C6F",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                px: 3,
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                borderRadius: "52px",
                backgroundColor: "#3F3D56",
                color: "#fff",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                px: 3,
                "&:hover": { backgroundColor: "#2F2C46" },
                "&:disabled": {
                  backgroundColor: "#E0E0E0",
                  color: "#9E9E9E",
                },
              }}
              disabled={(!isFormChanged && !isDirty) || isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};


ContactPharmacyInfo.propTypes = {
  contactInfo: PropTypes.object,
  pharmacyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSave: PropTypes.func,
};

export default ContactPharmacyInfo;
